import { startTransition, useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { orgUiPath } from '@/core/api';
import { useOrgId } from '@/hooks/useOrgId';
import { PANEL_QUERY, PANEL_RENDERS, parsePanelFromSearch } from '@/core/urlRouting';
import InsertAssetModal from '@/components/modals/InsertAssetModal';
import AssignCampaignModal from '@/components/modals/AssignCampaignModal';
import { getCreative, updateCreative } from '@/services/creatives';
import { listAssets, getAsset } from '@/services/assets';
import { formatPathIdFromStored, getTemplateInFormat } from '@/services/formats';
import { listCreativeRenders } from '@/services/renders';
import type { Creative as CreativeType, Asset, Template, RenderJobSummary } from '@/types/api';
import {
  asRecord,
  buildWorkingElements,
  deepClone,
  TemplateDrivenCanvas,
  type AssetFileShape,
} from '@/creativeComposition';
import { RenderJobList } from '@/components/app/RenderJobList';
import StartRenderModal from '@/components/modals/StartRenderModal';
import { IconButton } from '@/components/primitives/IconButton';

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function CreativeEditorPage() {
  const orgId = useOrgId();
  const { creativeId: creativeIdParam } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const creativeId = creativeIdParam != null ? parseInt(creativeIdParam, 10) : undefined;
  const isInvalidId = creativeId === undefined || Number.isNaN(creativeId);

  const [creative, setCreative] = useState<CreativeType | null>(null);
  const [template, setTemplate] = useState<Template | null>(null);
  const [workingElements, setWorkingElements] = useState<unknown[]>([]);
  const [assetsList, setAssetsList] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Rename state
  const [nameEditing, setNameEditing] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);

  // Modal state
  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [assetTargetIndex, setAssetTargetIndex] = useState<number | null>(null);
  const [insertAssetType, setInsertAssetType] = useState<'image' | 'video' | null>(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  // Renders state
  const [renderModalOpen, setRenderModalOpen] = useState(false);
  const rendersPanelOpen =
    parsePanelFromSearch(searchParams.toString()) === PANEL_RENDERS;
  const [creativeRenders, setCreativeRenders] = useState<RenderJobSummary[]>([]);
  const [rendersLoading, setRendersLoading] = useState(false);
  const [rendersError, setRendersError] = useState('');

  // ── Load creative + template + assets ──
  useEffect(() => {
    if (isInvalidId) return;
    startTransition(() => {
      setLoading(true);
      setError('');
    });
    getCreative(creativeId!)
      .then((c) => {
        setCreative(c);
        return getTemplateInFormat(formatPathIdFromStored(c.format_id), c.template_id).then((t) => {
          setTemplate(t);
          setWorkingElements(buildWorkingElements(t, c.elements));
        });
      })
      .then(() => listAssets().then((page) => setAssetsList(page.items)))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load creative'))
      .finally(() => setLoading(false));
  }, [creativeId, isInvalidId]);

  // ── Patch a working element immutably ──
  const patchEl = useCallback((index: number, fn: (el: Record<string, unknown>) => void) => {
    setWorkingElements((prev) => {
      const copy = deepClone(prev) as Record<string, unknown>[];
      fn(copy[index]);
      return copy;
    });
  }, []);

  // ── Handle asset selection from modal ──
  const handleAssetSelect = useCallback(
    async (assetId: number) => {
      if (assetTargetIndex == null || template == null) return;
      const el = asRecord(template.elements[assetTargetIndex]);
      const cur = asRecord(workingElements[assetTargetIndex]);
      const typ = String(cur?.element_type ?? el?.element_type ?? '');
      let asset = assetsList.find((a) => a.id === assetId);
      if (!asset) {
        try {
          const fetched = await getAsset(assetId);
          asset = fetched;
          if (fetched.id != null) {
            setAssetsList((prev) => {
              const without = prev.filter((a) => a.id !== fetched.id);
              return [fetched, ...without];
            });
          }
        } catch (err: unknown) {
          setError(err instanceof Error ? err.message : 'Failed to load selected asset');
          return;
        }
      }
      if (!asset || asset.id == null) return;
      if (typ === 'image' && asset.asset_type !== 'image') return;
      if (typ === 'video' && asset.asset_type !== 'video') return;
      const af: AssetFileShape = {
        uri: asset.uri,
        name: asset.name,
        asset_type: asset.asset_type,
        organization_id: asset.organization_id,
      };
      patchEl(assetTargetIndex, (row) => {
        const data = asRecord(row.element_data) ?? {};
        if (typ === 'image') {
          const ie = asRecord(data.image_element) ?? {};
          ie.asset_file = af;
          data.image_element = ie;
        } else if (typ === 'video') {
          const ve = asRecord(data.video_element) ?? {};
          ve.asset_file = af;
          data.video_element = ve;
        }
        row.element_data = data;
      });
      setAssetTargetIndex(null);
    },
    [assetTargetIndex, assetsList, patchEl, template, workingElements],
  );

  const handleAssetCreated = useCallback((asset: Asset) => {
    if (asset.id == null) return;
    setAssetsList((prev) => {
      const without = prev.filter((a) => a.id !== asset.id);
      return [asset, ...without];
    });
  }, []);

  // ── Save elements to API ──
  const handleSave = useCallback(() => {
    if (!creative?.id || isInvalidId) return;
    setSaving(true);
    setError('');
    updateCreative(creative.id, { elements: workingElements })
      .then(setCreative)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to save'))
      .finally(() => setSaving(false));
  }, [creative, isInvalidId, workingElements]);

  // ── Rename creative ──
  const handleRename = useCallback(
    (name: string) => {
      if (!creative?.id) return;
      updateCreative(creative.id, { name })
        .then((updated) => {
          setCreative(updated);
          setNameEditing(false);
          setNameError(null);
        })
        .catch((err) => setNameError(err instanceof Error ? err.message : 'Rename failed'));
    },
    [creative],
  );

  const setRendersPanelOpenTracked = useCallback(
    (open: boolean) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (open) {
            next.set(PANEL_QUERY, PANEL_RENDERS);
          } else {
            next.delete(PANEL_QUERY);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // ── Renders history ──
  const fetchCreativeRenders = useCallback(() => {
    if (creativeId == null || Number.isNaN(creativeId)) return;
    startTransition(() => {
      setRendersLoading(true);
      setRendersError('');
    });
    listCreativeRenders(creativeId)
      .then((page) => setCreativeRenders(page.items))
      .catch((err) =>
        setRendersError(err instanceof Error ? err.message : 'Failed to load renders'),
      )
      .finally(() => setRendersLoading(false));
  }, [creativeId]);

  useEffect(() => {
    if (!rendersPanelOpen || creativeId == null || Number.isNaN(creativeId)) return;
    fetchCreativeRenders();
  }, [rendersPanelOpen, creativeId, fetchCreativeRenders]);

  useEffect(() => {
    if (!rendersPanelOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setRendersPanelOpenTracked(false);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [rendersPanelOpen, setRendersPanelOpenTracked]);

  // ── Error / loading states ──
  if (isInvalidId) {
    return (
      <div className="aspectly:p-8">
        <p className="aspectly:text-error aspectly:font-medium">Invalid creative ID.</p>
        <Link
          to={orgUiPath(orgId, '/creatives')}
          className="aspectly:mt-4 aspectly:inline-flex aspectly:items-center aspectly:gap-2 aspectly:text-primary aspectly:font-bold aspectly:text-sm aspectly:hover:underline"
        >
          <span className="material-symbols-outlined aspectly:text-lg">arrow_back</span>
          Back to Creatives
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="aspectly:flex aspectly:items-center aspectly:justify-center aspectly:h-full aspectly:min-h-[60vh]">
        <span className="material-symbols-outlined aspectly:animate-spin aspectly:text-4xl aspectly:text-primary">
          progress_activity
        </span>
      </div>
    );
  }

  if (!creative || !template) {
    return (
      <div className="aspectly:h-full aspectly:flex aspectly:flex-col aspectly:items-center aspectly:justify-center aspectly:gap-4 aspectly:p-8">
        <span className="material-symbols-outlined aspectly:text-5xl aspectly:text-error/50">error</span>
        <p className="aspectly:text-on-surface-variant">{error || 'Creative not found.'}</p>
        <Link to={orgUiPath(orgId, '/creatives')} className="aspectly:text-primary aspectly:font-bold aspectly:text-sm aspectly:hover:underline">
          ← Back to Creatives
        </Link>
      </div>
    );
  }

  const unassigned = !creative.assigned_campaign_ids?.length;
  const backCampaignId = creative.assigned_campaign_ids?.[0];

  return (
    // Full viewport minus the AppShell header (4rem = 64px) — editor fills the rest
    <div className="aspectly:flex aspectly:flex-col" style={{ height: 'calc(100vh - 4rem)' }}>
      {/* ── Editor Top Bar ── */}
      <div className="aspectly:shrink-0 aspectly:bg-surface-container-lowest aspectly:border-b aspectly:border-outline-variant/10 aspectly:px-6 aspectly:py-3 aspectly:flex aspectly:items-center aspectly:justify-between aspectly:gap-4 aspectly:z-10">
        <div className="aspectly:flex aspectly:items-center aspectly:gap-4 aspectly:min-w-0 aspectly:flex-1">
          {/* Back button */}
          <button
            type="button"
            onClick={() =>
              navigate(
                backCampaignId != null
                  ? orgUiPath(orgId, `/campaigns/${backCampaignId}`)
                  : orgUiPath(orgId, '/creatives'),
              )
            }
            className="aspectly:flex aspectly:items-center aspectly:gap-1.5 aspectly:px-3 aspectly:py-1.5 aspectly:rounded-lg aspectly:text-on-surface-variant aspectly:hover:bg-surface-container aspectly:hover:text-on-surface aspectly:transition-all aspectly:text-sm aspectly:font-bold aspectly:cursor-pointer aspectly:shrink-0"
          >
            <span className="material-symbols-outlined aspectly:text-[18px]">arrow_back</span>
            <span className="aspectly:hidden aspectly:sm:inline">
              {backCampaignId != null ? 'Campaign' : 'Creatives'}
            </span>
          </button>

          {/* Creative name / inline rename */}
          {nameEditing ? (
            <div className="aspectly:flex aspectly:items-center aspectly:gap-2 aspectly:min-w-0 aspectly:max-w-sm aspectly:flex-wrap">
              <input
                autoFocus
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRename(nameValue.trim() || creative.name);
                  if (e.key === 'Escape') {
                    setNameEditing(false);
                    setNameError(null);
                  }
                }}
                className="aspectly:flex-1 aspectly:min-w-0 aspectly:px-3 aspectly:py-1.5 aspectly:rounded-lg aspectly:border aspectly:border-primary/40 aspectly:bg-surface-container-low aspectly:text-on-surface aspectly:text-sm aspectly:font-bold aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/30"
              />
              <button
                onClick={() => handleRename(nameValue.trim() || creative.name)}
                className="aspectly:p-1.5 aspectly:text-primary aspectly:cursor-pointer"
                aria-label="Save name"
              >
                <span className="material-symbols-outlined aspectly:text-lg">check</span>
              </button>
              <button
                onClick={() => {
                  setNameEditing(false);
                  setNameError(null);
                }}
                className="aspectly:p-1.5 aspectly:text-on-surface-variant aspectly:cursor-pointer"
                aria-label="Cancel"
              >
                <span className="material-symbols-outlined aspectly:text-lg">close</span>
              </button>
              {nameError && <p className="aspectly:w-full aspectly:text-xs aspectly:text-error">{nameError}</p>}
            </div>
          ) : (
            <div className="aspectly:flex aspectly:items-center aspectly:gap-2 aspectly:min-w-0">
              <span className="aspectly:font-headline aspectly:font-bold aspectly:text-on-surface aspectly:truncate">
                {creative.name}
              </span>
              <button
                onClick={() => {
                  setNameValue(creative.name);
                  setNameEditing(true);
                }}
                className="aspectly:p-1 aspectly:text-on-surface-variant aspectly:hover:text-on-surface aspectly:cursor-pointer aspectly:shrink-0"
                aria-label="Rename creative"
              >
                <span className="material-symbols-outlined aspectly:text-lg">edit</span>
              </button>
            </div>
          )}

          {/* Template badge */}
          <span className="aspectly:hidden aspectly:lg:flex aspectly:items-center aspectly:gap-1.5 aspectly:px-2.5 aspectly:py-1 aspectly:bg-surface-container aspectly:rounded-lg aspectly:text-xs aspectly:font-bold aspectly:text-on-surface-variant aspectly:border aspectly:border-outline-variant/20 aspectly:shrink-0">
            <span className="material-symbols-outlined aspectly:text-[14px]">tv</span>
            {template.name}
          </span>
        </div>

        {/* Action buttons */}
        <div className="aspectly:flex aspectly:items-center aspectly:gap-2 aspectly:shrink-0">
          <button
            type="button"
            onClick={() => setRendersPanelOpenTracked(!rendersPanelOpen)}
            className={`aspectly:flex aspectly:items-center aspectly:gap-1.5 aspectly:rounded-xl aspectly:h-9 aspectly:px-4 aspectly:text-sm aspectly:font-bold aspectly:transition-colors aspectly:cursor-pointer ${
              rendersPanelOpen
                ? 'aspectly:bg-primary-container aspectly:text-primary aspectly:border aspectly:border-primary/20' : 'aspectly:border aspectly:border-outline-variant/30 aspectly:text-on-surface-variant aspectly:hover:bg-surface-container aspectly:hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined aspectly:text-lg">history</span>
            <span className="aspectly:hidden aspectly:xl:inline">History</span>
          </button>

          <button
            type="button"
            onClick={() => setRenderModalOpen(true)}
            className="aspectly:flex aspectly:items-center aspectly:gap-1.5 aspectly:rounded-xl aspectly:h-9 aspectly:px-4 aspectly:border aspectly:border-primary/60 aspectly:text-primary aspectly:bg-primary/5 aspectly:hover:bg-primary/10 aspectly:transition-colors aspectly:text-sm aspectly:font-bold aspectly:cursor-pointer"
          >
            <span className="material-symbols-outlined aspectly:text-[17px]">movie</span>
            <span className="aspectly:hidden aspectly:sm:inline">Export</span>
          </button>

          <div className="aspectly:w-[1px] aspectly:h-6 aspectly:bg-outline-variant/30 aspectly:mx-1 aspectly:hidden aspectly:md:block" />

          {unassigned && (
            <button
              type="button"
              onClick={() => setAssignModalOpen(true)}
              className="aspectly:flex aspectly:items-center aspectly:gap-1.5 aspectly:rounded-xl aspectly:h-9 aspectly:px-4 aspectly:border aspectly:border-outline-variant/30 aspectly:text-on-surface-variant aspectly:text-sm aspectly:font-bold aspectly:hover:bg-surface-container aspectly:hover:text-on-surface aspectly:transition-colors aspectly:cursor-pointer"
            >
              <span className="material-symbols-outlined aspectly:text-lg">link</span>
              <span className="aspectly:hidden aspectly:sm:inline">Assign Campaign</span>
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="aspectly:flex aspectly:items-center aspectly:gap-1.5 aspectly:rounded-xl aspectly:h-9 aspectly:px-5 aspectly:bg-primary aspectly:text-on-primary aspectly:text-sm aspectly:font-bold aspectly:hover:bg-primary-dim aspectly:transition-colors aspectly:cursor-pointer aspectly:disabled:opacity-60"
          >
            {saving ? (
              <>
                <span className="material-symbols-outlined aspectly:text-lg aspectly:animate-spin">
                  progress_activity
                </span>
                <span className="aspectly:hidden aspectly:sm:inline">Saving…</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined aspectly:text-lg">save</span>
                Save
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── Canvas Row ── */}
      <div className="aspectly:flex-1 aspectly:min-h-0 aspectly:relative aspectly:overflow-x-hidden aspectly:flex aspectly:border-t aspectly:border-outline-variant/10">
        <div className="aspectly:flex-1 aspectly:min-w-0 aspectly:min-h-0 aspectly:flex aspectly:flex-col aspectly:bg-surface-container-low">
          <TemplateDrivenCanvas
            template={template}
            workingElements={workingElements}
            assetsList={assetsList}
            patchEl={patchEl}
            onPickImage={(idx) => {
              setAssetTargetIndex(idx);
              setInsertAssetType('image');
              setAssetModalOpen(true);
            }}
            onPickVideo={(idx) => {
              setAssetTargetIndex(idx);
              setInsertAssetType('video');
              setAssetModalOpen(true);
            }}
          />
        </div>

        {/* Sidebar panel for renders */}
        {rendersPanelOpen && (
          <div
            className="aspectly:absolute aspectly:inset-0 aspectly:z-40 aspectly:bg-inverse-surface/30 aspectly:backdrop-blur-sm aspectly:xl:hidden"
            onClick={() => setRendersPanelOpenTracked(false)}
          />
        )}
        <aside
          className={`aspectly:absolute aspectly:top-0 aspectly:right-0 aspectly:bottom-0 aspectly:z-50 aspectly:flex aspectly:w-full aspectly:max-w-md aspectly:flex-col aspectly:border-l aspectly:border-outline-variant/20 aspectly:bg-surface-container-lowest aspectly:shadow-2xl aspectly:transition-transform aspectly:duration-300 aspectly:ease-in-out aspectly:xl:relative aspectly:xl:shadow-none ${
            rendersPanelOpen
              ? 'aspectly:translate-x-0 aspectly:pointer-events-auto aspectly:xl:w-[32rem]'
              : 'aspectly:translate-x-full aspectly:pointer-events-none aspectly:xl:hidden aspectly:xl:w-0 aspectly:border-l-0'
          }`}
        >
          {rendersPanelOpen && (
            <>
              <div className="aspectly:shrink-0 aspectly:flex aspectly:items-center aspectly:justify-between aspectly:gap-3 aspectly:px-5 aspectly:py-4 aspectly:border-b aspectly:border-outline-variant/10 aspectly:bg-surface-container-lowest">
                <div className="aspectly:flex aspectly:items-center aspectly:gap-2.5 aspectly:min-w-0">
                  <span className="material-symbols-outlined aspectly:text-primary aspectly:shrink-0 aspectly:text-xl">
                    history
                  </span>
                  <span className="aspectly:text-sm aspectly:font-headline aspectly:font-bold aspectly:text-on-surface aspectly:truncate">
                    Render History
                  </span>
                </div>
                <div className="aspectly:flex aspectly:items-center aspectly:gap-1 aspectly:shrink-0">
                  <IconButton
                    icon="refresh"
                    size="sm"
                    variant="ghost"
                    onClick={() => fetchCreativeRenders()}
                  />
                  <IconButton
                    icon="close"
                    size="sm"
                    variant="ghost"
                    onClick={() => setRendersPanelOpenTracked(false)}
                  />
                </div>
              </div>
              <div className="aspectly:flex-1 aspectly:min-h-0 aspectly:overflow-y-auto aspectly:p-5 aspectly:bg-surface-container-lowest">
                {rendersError && (
                  <div className="aspectly:mb-4 aspectly:p-3 aspectly:rounded-lg aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm aspectly:font-medium">
                    {rendersError}
                  </div>
                )}
                {rendersLoading ? (
                  <div className="aspectly:flex aspectly:justify-center aspectly:py-16">
                    <span className="material-symbols-outlined aspectly:animate-spin aspectly:text-3xl aspectly:text-primary">
                      progress_activity
                    </span>
                  </div>
                ) : (
                  <RenderJobList
                    rows={creativeRenders}
                    emptyMessage="No renders for this creative yet. Use Export to start."
                  />
                )}
              </div>
            </>
          )}
        </aside>
      </div>

      {/* ── Footer ── */}
      <footer className="aspectly:shrink-0 aspectly:border-t aspectly:border-outline-variant/10 aspectly:bg-surface-container-lowest aspectly:px-6 aspectly:py-3 aspectly:flex aspectly:items-center aspectly:justify-between aspectly:gap-4 aspectly:text-xs aspectly:text-on-surface-variant">
        <div className="aspectly:flex aspectly:items-center aspectly:gap-2">
          <span className="material-symbols-outlined aspectly:text-base">aspect_ratio</span>
          <span>Canvas scales with viewport. Export resolutions are a future render step.</span>
        </div>
        <span className="aspectly:font-medium">{template.name}</span>
      </footer>

      {/* ── Modals ── */}
      <InsertAssetModal
        isOpen={assetModalOpen}
        onClose={() => {
          setAssetModalOpen(false);
          setAssetTargetIndex(null);
          setInsertAssetType(null);
        }}
        onSelect={handleAssetSelect}
        onAssetCreated={handleAssetCreated}
        allowedAssetType={insertAssetType ?? undefined}
      />
      <AssignCampaignModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        creativeId={creative.id ?? null}
        creativeName={creative.name}
        onAssigned={() => {
          getCreative(creativeId!)
            .then(setCreative)
            .catch((err: unknown) =>
              setError(
                err instanceof Error ? err.message : 'Failed to refresh creative after assignment',
              ),
            );
        }}
      />
      {creative.id != null && (
        <StartRenderModal
          isOpen={renderModalOpen}
          onClose={() => setRenderModalOpen(false)}
          creativeId={creative.id}
          creativeName={creative.name}
          onStarted={() => {
            if (rendersPanelOpen) fetchCreativeRenders();
          }}
        />
      )}
    </div>
  );
}
